import { Injectable, Inject } from '@angular/core';
import { Router, Event } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { MemberAccountDetailsResponse } from 'app/auth/member-account-details';
import { BroadcastMessages, HolisticMemberProfile, HolisticMemberProfileResponse } from 'app/auth/holistic-profile';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { EventService } from 'app/shared/event.service';
import { memberAccountDetailsInterface } from '../claims-summary/interfaces/memberAccountDetails.interface';

const DATA_LOADED_EVENT_NAME = 'ON_DATA_LOADED';

/**
 * User session is actually an injectible service.
 * This service/object will play below roles:
 * 1) Representing the current authentication state/status  (to replace authService)
 * 2) Hold the user holistic profile data after user logged in, can be accessed by any feature module
 * 3) Hold the deeplink information, route user to the deeplink after login/registeration.
 * 4) Provide several interfaces so that developers can use to save information in cookie/memory/local storage,
 * this is important that every developer should use userSession to access these data, then we can clean these data when logout by simply calling a function.
 */

@Injectable()
export class UserSession implements memberAccountDetailsInterface{
  private cookieNames = new Set<string>(); // used for tracking deletion purpose only
  private localStorageItemNames = new Set<string>(); // used for tracking deletion purpose only
  private sessionData = new Map<string, any>();
  private _deeplinkUrl: string;
  private memberProfile: HolisticMemberProfile;
  private memberAccountDetails: MemberAccountDetailsResponse;
  // Changes for Broadcast Message
  private broadcastMessage: BroadcastMessages[];
  private currentAccountId: string;
  public fapSessionId: number;
  public fapVersion: number;
  public promoPageTaken: boolean = false;
  public newloginSession: boolean = false;

  // the 3rd parameter gives an easy way for unit testing
  constructor(
    private cookieService: CookieService,
    private eventService: EventService,
    @Inject('LOCALSTORAGE') private localStorage: any
  ) {}

  getCurrentAccountId() {
    return this.currentAccountId;
  }

  setCurrentAccountId(currentAccountId: string) {
    this.currentAccountId = currentAccountId;
  }

  getMemberAccountDetails(): MemberAccountDetailsResponse {
    return this.memberAccountDetails;
  }

  setMemberAccountDetails(memberAccountDetailsResponse: MemberAccountDetailsResponse) {
    this.memberAccountDetails = memberAccountDetailsResponse;
  }

  get isDataLoaded(): boolean {
    return this.memberProfile != null && this.memberProfile !== undefined;
  }

  setMemberProfile(memberProfileResponse: HolisticMemberProfileResponse) {
    this.memberProfile = memberProfileResponse.holisticMemberProfile;
    // Changes for Breoadcast Message
    this.setBroadcastMessage(memberProfileResponse);
    // below event is published in FetchUserDataComponent after adding additional client flags
    // this.eventService.publish(DATA_LOADED_EVENT_NAME, this.isDataLoaded);
  }

  getMemberProfile() {
    if (!this.memberProfile) {
      throw Error(
        'Please access member Profile on ngInit only, if it is a service, you should not access it inside constructor or directly used to assign local value'
      );
    }
    return this.memberProfile;
  }

  // Setting broadCastMessages
  setBroadcastMessage(memberProfileResponse: HolisticMemberProfileResponse) {
    this.broadcastMessage = memberProfileResponse.broadcastMessages;
  }

  //Get BroadcastMessages
  getBroadcastMessage() {
    return this.broadcastMessage;
  }
  // Shortcut for member attributes
  getMemberAttributes() {
    return this.getMemberProfile().memberAttributes;
  }

  // Shortcut for member feature flags
  getMemberFeatureFlags() {
    return this.getMemberProfile().memberFeatureFlags;
  }

  // Shortcut for member coverage flags
  getMemberCoverageFlags() {
    return this.getMemberProfile().memberCoverageFlags;
  }

  get deeplinkUrl() {
    return this._deeplinkUrl;
  }

  set deeplinkUrl(url: string) {
    this._deeplinkUrl = url;
  }

  clearData() {
   
    /**
     * deleting the accountId cookie explicitly so that it gets deleted on login page,
     * For when the user does not logout and just closes the browser
     */ 
    this.cookieService.delete('accountId');
    this._deeplinkUrl = null;
    this.memberProfile = null;
    // Changes for Broadcast Message
    this.broadcastMessage = null;
    this.memberAccountDetails = null;
    this.currentAccountId = null;
    this.cookieNames.forEach((value, key) => {
      this.cookieService.delete(key);
    });
    this.localStorageItemNames.forEach((value, key) => {
      this.localStorage.removeItem(key);
    });

    // reset
    this.localStorageItemNames = new Set<string>();
    this.cookieNames = new Set<string>();
    this.sessionData = new Map<string, any>();

    this.eventService.publish(DATA_LOADED_EVENT_NAME, this.isDataLoaded);
    this.isFapSession  = null;
    this.isFapVersion = null;
  }

  setCookie(name: string, value: string) {
    if (name && value) {
      this.cookieNames.add(name);
      this.cookieService.set(name, value);
    }
  }

  getCookie(name): string {
    return this.cookieService.get(name);
  }

  deleteCookie(key:string) {
    if (key) {
      this.cookieService.delete(key);
    }
  }

  setLocalStorageItem(key: string, value, options?) {
    // option to persist so that the item
    // will not be removed even on clearData()
    if (!(options && options.persist)) {
      this.localStorageItemNames.add(key);
    }
    this.localStorage.setItem(key, value);
  }

  getLocalStorageItem(key) {
    return this.localStorage.getItem(key);
  }

  removeLocalStorageItem(key) {
    this.localStorage.removeItem(key);
    delete this.localStorageItemNames[key];
  }

  setSession(name: string, value: Object) {
    this.sessionData[name] = value;
  }

  // Save data in memory only, will be disappear if user refresh browser.
  getSession(name: string): Object {
    return this.sessionData[name];
  }

  onDataLoaded(callback: (isLoaded: boolean) => void) {
    this.setSession('currentPassword', '');
    this.eventService.subscribe(DATA_LOADED_EVENT_NAME, callback);
  }

  // Setting Fap Session Id in user-session 
  set isFapSession(session) {
    this.fapSessionId = session;
  }

  get isFapSession(){
    return this.fapSessionId;
  }  

   // Setting Fap Version in user-session 
   set isFapVersion(version) {
     this.fapVersion = version;
  }

  get isFapVersion(){
    return this.fapVersion;
  }  

  promptAccountRecoveryPromo(){
    return this.getMemberFeatureFlags().showMultiFactorAuthConsent && this.isConfirmedContactEmailOrPhone() && ( this.getMemberAttributes().multiFactorConsent === 'NA' || this.getMemberAttributes().multiFactorConsent === null);
  }

  isConfirmedContactEmailOrPhone(){
    return this.getMemberFeatureFlags().contactEmailConfirmed || this.getMemberFeatureFlags().contactPhoneConfirmed;
  }
    
  hasPromoPageTaken(){
    
    return this.promoPageTaken;
  }

  hasnewloginSessio(){
    return this.newloginSession;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/user-session.ts